import {ParsedUrlQuery} from "querystring";

import React from "react";
import Head from "next/head";
import {DEFAULT_LOCALE, LOCALES} from "@pency/constants/catalogs/i18n";
import {serverSideTranslations} from "next-i18next/serverSideTranslations";

import {Stack, Text} from "@chakra-ui/core";
import Button from "@pency/components/controls/Button";
import Image from "@pency/components/display/Image";
import {GetServerSideProps} from "next";
import UnstyledLink from "@pency/components/controls/Link";
import NextImage from 'next/image'

import {META} from "../constants";

import SocialLinks from "~/tenant/components/SocialLinks";
import api from "~/tenant/api";
import {Tenant, Link as LinkInterface} from "~/tenant/types";
import {Provider as TenantProvider} from "~/tenant/context";
import Link from "~/ui/controls/Link";

import {useTranslation} from "~/i18n/hooks";

interface Params extends ParsedUrlQuery {
  slug: Tenant["slug"];
}

interface Props {
  tenant: Tenant;
} 

const formatLinks = (links,urlStrictValidation: (url:String) => boolean, storeLink:String) => {
  return links.map((link) => {
    return {
      ...link,
      url: urlStrictValidation(link.url)
        ? storeLink 
        : link.url
    }
  })
}

const SlugRoute: React.FC<Props> = ({tenant}) => {
  const storeLink = `${process.env.STORE_URL}/${tenant.slug}`;
  const t = useTranslation();
  const urlStrictValidation = (url:String) => url === storeLink || url === process.env.STORE_URL
  const hasStoreLink = tenant.links.find((link) => urlStrictValidation(link.url))
  const links = 
    hasStoreLink 
      ? formatLinks(tenant.links, urlStrictValidation, storeLink) 
      : [{url: storeLink, label: t('links.site')}];

  return (
    <>
      <Head>
        <title>
          {tenant.title || META.title} (@{tenant.slug}) / Página de links Pency
        </title>
        <meta content={tenant.description || META.description} name="description" />
        {tenant.banner && <link as="image" href={tenant.banner} rel="preload" />}
        {tenant.logo && <link as="image" href={tenant.logo} rel="preload" />}
        <link
          as="font"
          crossOrigin="anonymous"
          href="https://fonts.gstatic.com/s/inter/v3/UcC73FwrK3iLTeHuS_fvQtMwCp50KnMa1ZL7W0Q5nw.woff2"
          rel="preload"
        />
        <link href={META.favicon} rel="icon" />
        <link href={tenant.logo || META.appleicon} rel="apple-touch-icon" />
        <meta content={tenant.color || META.theme} name="theme-color" />
        <meta content={tenant.keywords} name="keywords" />
        <meta content={META.author} name="author" />
        <meta content={META.author} property="og:site_name" />
        <meta content={META.fbapp} property="fb:app_id" />
        <meta content="summary_large_image" name="twitter:card" />
        <meta
          content={tenant.twitter ? `@${tenant.twitter}` : META.twitter}
          name="twitter:creator"
        />
        <meta content={META.twitter} name="twitter:site" />
        <meta
          content="width=device-width, initial-scale=1.0, viewport-fit=contain"
          name="viewport"
        />
        <link href={`${META.url}/${tenant.slug}`} rel="canonical" />
        <meta content={tenant.title} name="twitter:title" />
        <meta content={tenant.description || META.description} name="twitter:description" />
        <meta content={tenant.banner || tenant.logo || META.banner?.url} property="twitter:image" />
        <meta content={`${META.url}/${tenant.slug}`} property="og:url" />
        <meta content="website" property="og:type" />
        <meta content={tenant.title ? `Pency | ${tenant.title}` : META.title} property="og:title" />
        <meta content={tenant.description || META.description} property="og:description" />
        <meta content={tenant.banner || tenant.logo || META.banner?.url} property="og:image" />
        <meta
          content={tenant.banner || tenant.logo || META.banner?.url}
          property="og:image:secure"
        />
        <meta content={tenant.banner || tenant.logo || META.banner?.url} property="og:image:url" />
        <meta content={META.banner?.format} property="og:image:type" />
        <meta content={META.banner?.width} property="og:image:width" />
        <meta content={META.banner?.height} property="og:image:height" />
        <meta content={tenant.title} property="og:image:alt" />
        <meta
          content="width=device-width, initial-scale=1.0, viewport-fit=contain"
          name="viewport"
        />
        {tenant?.meta?.map((meta, index) => (
          <meta key={meta.property || index} {...meta} />
        ))}
      </Head>
      <TenantProvider initialValue={tenant}>
        <Stack alignItems="center" marginX="auto" maxWidth={640} minHeight="100%" spacing={12}>
          <Stack spacing={0} width="100%">
            <Image
              borderBottomLeftRadius="lg"
              borderBottomRightRadius="lg"
              height={32}
              src={tenant.banner}
            />
            <Stack alignItems="center" paddingX={4} spacing={3}>
              <Image
                borderRadius="50%"
                boxShadow="inset 0 0 2px rgba(0,0,0,0.2), 0px 0px 0px 3px white, 0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)"
                height={32}
                marginTop={-16}
                src={tenant.logo}
                width={32}
              />
              <Text fontSize="2xl" fontWeight={700} lineHeight="normal" textAlign="center">
                {tenant.title}
              </Text>
              <SocialLinks
                facebook={tenant.facebook}
                instagram={tenant.instagram}
                twitter={tenant.twitter}
                whatsapp={tenant.phone}
              />
            </Stack>
          </Stack>
          <Stack spacing={6} width="100%">
            {links.length ? (
              links.map((link: LinkInterface) => {
                if(link?.display !== false) {
                  return (
                    <Link key={link.url} href={hasStoreLink
                      ? link.url 
                      : t(link.url)}
                      >
                      {t(link.label)}
                    </Link>
                  )}
                })
            ) : (
              <Button isDisabled size="lg" variant="outline" variantColor="primatu">
                No hay links
              </Button>
            )}
          </Stack>
          <UnstyledLink isExternal href={process.env.LANDING_URL} marginTop="auto" padding={4}>
            <Stack isInline alignItems="center" spacing={1}>
              <Text>{t("links.poweredBy")}</Text>
              <NextImage 
                priority
                height='18x' 
                width='90px' 
                alt='Peny Logo'
                src='/assets/logo/pency-logo.png' 
              />
            </Stack>
          </UnstyledLink>
        </Stack>
      </TenantProvider>
    </>
  );
};

export const getServerSideProps: GetServerSideProps<any, Params> = async ({params: {slug}}) => {
  try {
    // Get the tenant for this page slug
    const tenant: Tenant = await api.fetch(slug);

    const storeLocale = LOCALES[tenant.country];
    const availableLocales = Object.values(LOCALES);
    const currentLocale = availableLocales.includes(storeLocale) ? storeLocale : DEFAULT_LOCALE;

    // Return the props and revalidation times
    return {
      props: {
        ...(await serverSideTranslations(currentLocale)),
        tenant
      },
    };
  } catch (err) {
    return {
      // If something failed return a status code that will be intercepted by _app
      props: {
        ...(await serverSideTranslations(DEFAULT_LOCALE)),
        statusCode: err?.status || err?.statusCode || 404,
      },
    };
  }
};

export default SlugRoute;
